import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useShop } from "../context/ShopContext";
import { scrollToSection } from "../lib/scroll";

const HERO_IMAGE = "/uploads/hero-cabinet.png";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const { t, lang, cval, contentImage } = useShop();
  const navigate = useNavigate();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const heroImage = contentImage("hero") || HERO_IMAGE;
  const lines = [1, 2, 3].map((n) => cval(`hero_line${n}_${lang}`, t.hero.lines[n - 1]));

  return (
    <section
      ref={ref}
      className="relative min-h-screen flex items-end overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
        <img
          src={heroImage}
          alt="Ahşap dokulu PVC kombi dolabı, modern iç mekân"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-cream/95 via-cream/55 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream/90 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ opacity: contentOpacity }}
        className="relative z-10 max-w-[1440px] mx-auto w-full px-6 md:px-12 pb-24 md:pb-32 pt-40"
      >
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.35 }}
          className="text-xs tracking-[0.3em] uppercase font-semibold text-olive-dark mb-8"
          data-testid="hero-overline"
        >
          {t.hero.overline && (
            <>{cval(`hero_overline_${lang}`, t.hero.overline)}</>
          )}
        </motion.p>

        <h1
          className="font-serif text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.02] text-brown max-w-3xl"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.5}>{lines[0]}</MaskedLine>
          <MaskedLine delay={0.65}>{lines[1]}</MaskedLine>
          <MaskedLine delay={0.8}>
            <span className="italic text-olive-dark">{lines[2]}</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-8 max-w-xl text-base md:text-lg font-light leading-relaxed text-brown-light"
          data-testid="hero-subheadline"
        >
          {cval(`hero_sub_${lang}`, t.hero.sub)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.2 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => navigate("/urunler")}
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-brown text-cream text-sm font-medium tracking-wide hover:bg-olive-dark transition-colors duration-300"
            data-testid="hero-cta-primary"
          >
            {t.hero.ctaPrimary}
            <ArrowRight
              size={16}
              className="transition-transform duration-300 group-hover:translate-x-1"
            />
          </button>
          <button
            onClick={() => scrollToSection("#b2b")}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-full border border-brown/30 text-brown text-sm font-medium tracking-wide hover:border-olive hover:text-olive-dark hover:bg-olive/5 transition-colors duration-300"
            data-testid="hero-cta-secondary"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.5 }}
          className="mt-12 flex flex-wrap gap-3"
          data-testid="hero-chips"
        >
          {t.hero.chips.map((chip) => (
            <span
              key={chip}
              className="px-4 py-1.5 rounded-full border border-brown/15 text-xs tracking-widest uppercase text-brown-light"
            >
              {chip}
            </span>
          ))}
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-brown-muted"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase [writing-mode:vertical-rl]">
          {t.hero.scroll}
        </span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};

export default Hero;
